/**
 * @file KPICard.tsx
 * @description Animated KPI metric card with trend indicator and color coding.
 *              Used across the Ops Overview view.
 */

import { LucideIcon, TrendingUp, TrendingDown, Minus } from "lucide-react";

interface KPICardProps {
  id: string;
  label: string;
  value: string | number;
  unit?: string;
  subtext?: string;
  icon: LucideIcon;
  trend?: "up" | "down" | "neutral";
  trendLabel?: string;
  /** Color variant: "teal" | "red" | "amber" | "purple" | "blue" */
  variant?: "teal" | "red" | "amber" | "purple" | "blue";
  loading?: boolean;
}

const VARIANT_STYLES = {
  teal: {
    icon: "bg-teal-500/15 text-teal-400",
    value: "text-teal-400",
    border: "border-teal-500/20",
    glow: "shadow-glow",
  },
  red: {
    icon: "bg-red-500/15 text-red-400",
    value: "text-red-400",
    border: "border-red-500/20",
    glow: "shadow-glow-red",
  },
  amber: {
    icon: "bg-amber-500/15 text-amber-400",
    value: "text-amber-400",
    border: "border-amber-500/20",
    glow: "",
  },
  purple: {
    icon: "bg-purple-500/15 text-purple-400",
    value: "text-purple-400",
    border: "border-purple-500/20",
    glow: "",
  },
  blue: {
    icon: "bg-blue-500/15 text-blue-400",
    value: "text-blue-400",
    border: "border-blue-500/20",
    glow: "",
  },
};

export function KPICard({
  id,
  label,
  value,
  unit,
  subtext,
  icon: Icon,
  trend,
  trendLabel,
  variant = "teal",
  loading = false,
}: KPICardProps) {
  const styles = VARIANT_STYLES[variant];

  const TrendIcon = trend === "up" ? TrendingUp : trend === "down" ? TrendingDown : Minus;
  const trendColor =
    trend === "up"
      ? "text-green-400"
      : trend === "down"
      ? "text-red-400"
      : "text-gray-500";

  return (
    <div
      id={id}
      className={`relative rounded-2xl border ${styles.border} p-5 overflow-hidden transition-all duration-300 hover:scale-[1.01] group`}
      style={{
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.055) 0%, rgba(255,255,255,0.02) 100%)",
        backdropFilter: "blur(8px)",
      }}
    >
      {/* Subtle top highlight line */}
      <div
        className="absolute inset-x-0 top-0 h-px opacity-50"
        style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)" }}
      />

      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-500 uppercase tracking-widest font-medium mb-3">
            {label}
          </p>

          {loading ? (
            <div className="h-9 w-24 rounded-lg bg-white/5 animate-pulse" />
          ) : (
            <div className="flex items-baseline gap-1.5">
              <span className={`text-3xl font-bold ${styles.value} tabular-nums`}>
                {value}
              </span>
              {unit && (
                <span className="text-sm text-gray-500 font-medium">{unit}</span>
              )}
            </div>
          )}

          {subtext && !loading && (
            <p className="mt-1.5 text-xs text-gray-600">{subtext}</p>
          )}

          {trend && trendLabel && !loading && (
            <div className={`mt-2 flex items-center gap-1.5 ${trendColor}`}>
              <TrendIcon className="w-3.5 h-3.5" />
              <span className="text-xs font-medium">{trendLabel}</span>
            </div>
          )}
        </div>

        <div className={`flex-shrink-0 p-3 rounded-xl ${styles.icon} transition-transform duration-300 group-hover:scale-110`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}
